import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, InputSignal, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Interest } from '../../../interfaces/register-user.response';
import { FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseFormField } from '../../form/abstracts';
import { MatError } from '@angular/material/form-field';
import { InterestsFieldService } from './services/interests-field.service';

@Component({
  selector: 'app-interests-field',
  standalone: true,
  imports: [
    TranslateModule,
    MatError
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi:true,
      useExisting: InterestsFieldComponent
    },
  ],
  templateUrl: './interests-field.component.html',
  styleUrls: ['./interests-field.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InterestsFieldComponent extends BaseFormField<Interest[]> {
  // Inputs
  public form: InputSignal<FormGroup> = input.required<FormGroup>();
  public formControlName: InputSignal<string> = input.required<string>();
  
  // Services and utilities
  private _cdr = inject(ChangeDetectorRef);
  private _interestsFieldService = inject(InterestsFieldService);
  
  // Computed properties
  private _formControlName = computed(() => this.form().get(this.formControlName()));
  
  // Local signals
  // TODO: PARA CICERONEWEB-SSR NO HARÁ FALTA SETEARLO POR EFFECT SEGÚN 
  // TODO: EL IDIOMA YA QUE CUANDO SE CAMBIA DE IDIOMA RECARGA LA PÁGINA
  // TODO: VIENE POR URL
  protected interestListChips = signal<Interest[]>([]);
  
  // Effects
  /**
   * Sets up the interest list with alphabetical ordering
   * Maintains exact same logic as original, delegated to service
   */
  setInterestListChips = effect(() => {
    const orderedInterests = this._interestsFieldService.getOrderedInterests();
    this.interestListChips.set(orderedInterests);
  }, { allowSignalWrites: true });
  
  /**
   * Marks all form fields as touched for desktop validation
   * Maintains exact same logic as original, delegated to service
   */
  markAllFieldsAsTouched = effect(() => {
    const control = this._formControlName();
    this._interestsFieldService.markFieldsAsTouchedForDesktop(control, this._cdr);
  }, { allowSignalWrites: true });
   
  // Methods
  /**
   * Handles chip set interests change event
   * Maintains exact same logic as original, delegated to service
   * @param event - The change event from the chip set
   */
  public handleChipSetInterestsChange(event: Event) {
    const customEvent = event as CustomEvent<{ values: number[] }>;
    const selectedInterests = customEvent.detail.values;
    
    const control = this.form().get(this.formControlName());
    this._interestsFieldService.handleInterestsSelection(selectedInterests, control);
  }
  
  /**
   * Checks if a specific interest is currently selected
   * Maintains exact same logic as original, delegated to service
   * @param id - Interest ID to check
   * @returns True if the interest is selected
   */
  public isSelectedInterest(id: number): boolean {
    return this._interestsFieldService.isSelectedInterest(id);
  }
  
  /**
   * Validates if the field has errors and should display them
   * Maintains exact same validation logic as original, delegated to service
   * @returns True if field is invalid and should show errors
   */
  public isInvalidField(): boolean {
    const control = this._formControlName();
    return this._interestsFieldService.validateField(control);
  }
}
